import { CreditCard, PayoffPlan, PlanMode } from '../../types';
import { ScheduleResult } from '../../utils/finance/planMath';
import { CardPaymentEvent } from '../../utils/finance/cardLedger';

/** Convert a locally-generated schedule into the POST/PUT body.
 *  The server re-validates every invariant (monotonic dates, non-negative
 *  amounts, opening + interest − amount === closing, chained balances). */
export function toPlanPayload(input: {
  card: CreditCard;
  mode: PlanMode;
  result: ScheduleResult;
  name?: string | null;
  targetDate?: Date | null;
  targetPayment?: number | null;
  originBalance: number;
  originAsOf: Date;
  isStale?: boolean;
}) {
  const { card, mode, result } = input;
  return {
    creditCardId: card.id,
    name: input.name ?? null,
    mode,
    targetDate: input.targetDate ? input.targetDate.toISOString() : null,
    targetPayment: input.targetPayment ?? null,
    originBalance: input.originBalance,
    originAsOf: input.originAsOf.toISOString(),
    apr: card.interestRate,
    minFloor: card.minimumPayment,
    principalPct: card.minPaymentPrincipalPercent ?? 1,
    dueDay: card.dueDay ?? null,
    isFeasible: result.isFeasible,
    isStale: input.isStale ?? false,
    payments: result.payments.map(p => ({
      sequence: p.sequence,
      dueDate: p.dueDate.toISOString(),
      kind: p.kind,
      amount: p.amount,
      generatedAmount: p.generatedAmount,
      isCustomized: p.isCustomized,
      openingBalance: p.openingBalance,
      interestAccrued: p.interestAccrued,
      principalApplied: p.principalApplied,
      closingBalance: p.closingBalance,
    })),
  };
}

/** Turn an unsaved candidate schedule into ledger events so the builder can
 *  run the threshold check against a real cash projection before activating. */
export function draftCardEvents(
  card: CreditCard,
  result: ScheduleResult
): CardPaymentEvent[] {
  return result.payments.map((p, i) => ({
    cardId: card.id,
    cardName: card.name,
    planId: 'draft',
    paymentId: `draft-${i}`,
    date: p.dueDate,
    amount: p.amount,
    paymentKind: p.kind === 'EXTRA' ? 'EXTRA' : p.kind === 'FINAL' ? 'FINAL' : 'SCHEDULED',
    status: 'PLANNED' as const,
    openingBalance: p.openingBalance,
    interestAccrued: p.interestAccrued,
    remainingAfter: p.closingBalance,
    isFinal: p.kind === 'FINAL',
  }));
}

/** Round-trip an existing plan back into a PUT body unchanged. Used by the
 *  "leave the plan alone" branch of the balance-reconcile dialog, which needs
 *  to flip `isStale` without regenerating the schedule. */
export function planToPayload(plan: PayoffPlan) {
  return {
    creditCardId: plan.creditCardId,
    name: plan.name ?? null,
    mode: plan.mode,
    targetDate: plan.targetDate ? new Date(plan.targetDate).toISOString() : null,
    targetPayment: plan.targetPayment ?? null,
    originBalance: plan.originBalance,
    originAsOf: new Date(plan.originAsOf).toISOString(),
    apr: plan.apr,
    minFloor: plan.minFloor,
    principalPct: plan.principalPct,
    dueDay: plan.dueDay ?? null,
    isFeasible: plan.isFeasible,
    payments: plan.payments.map(p => ({
      sequence: p.sequence,
      dueDate: new Date(p.dueDate).toISOString(),
      kind: p.kind,
      amount: p.amount,
      generatedAmount: p.generatedAmount ?? null,
      isCustomized: p.isCustomized,
      openingBalance: p.openingBalance,
      interestAccrued: p.interestAccrued,
      principalApplied: p.principalApplied,
      closingBalance: p.closingBalance,
      status: p.status,
      confirmedAt: p.confirmedAt ? new Date(p.confirmedAt).toISOString() : null,
      actualAmount: p.actualAmount ?? null,
    })),
  };
}

export function planSummaryLine(plan: PayoffPlan): string {
  if (plan.mode === 'LUMP_SUM') {
    const p = plan.payments[0];
    return p
      ? `${usd(p.amount)} on ${new Date(p.dueDate).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`
      : 'One-time payment';
  }
  const regular = plan.payments.find(p => p.kind === 'SCHEDULED');
  const monthly = regular ? usd(regular.amount) : '—';
  return `${monthly}/mo · payoff ${plan.endDate.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })} · ${usd(plan.totalInterest)} interest`;
}

export function usd(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
